import { Button } from './app-ui/button';
import { ArrowLeft, LoaderCircle, RefreshCw, UserRound } from "./app-ui/icons/index";
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Brand from './Brand';
import './account-recovery.css';

export default function AccountRecovery() {
  const { t } = useTranslation();
  const { user, loading, authError, retryAccount, signOut } = useAuth();
  const location = useLocation();
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  const operation = useRef(0);
  useEffect(() => { operation.current += 1; setBusy(false); setError(''); return () => { operation.current += 1; }; }, [user?.uid]);
  async function switchAccount() {
    if (busy) return;
    const attempt = ++operation.current;
    setBusy(true); setError('');
    try { await signOut(); }
    catch { if (operation.current === attempt) setError(t('appEntry.signOutError')); }
    finally { if (operation.current === attempt) setBusy(false); }
  }
  return <main className="account-recovery-page">
    <Link to="/start" className="back-link"><ArrowLeft />{t('common.back')}</Link>
    <section className="account-recovery-card" aria-busy={loading}>
      <Brand />
      <div className="account-recovery-symbol" aria-hidden>{loading ? <LoaderCircle className="spin" /> : <UserRound />}</div>
      <h1>{t(loading ? 'appEntry.accountLoading' : user ? 'appEntry.accountError' : 'appEntry.startupError')}</h1>
      <p role={loading ? 'status' : authError ? 'alert' : undefined}>{t(loading ? 'appEntry.accountLoadingBody' : user ? 'appEntry.accountErrorBody' : 'appEntry.startupErrorBody')}</p>
      {user?.email && <p className="account-recovery-email">{user.email}</p>}
      {authError && !loading && <Button variant="default" size="touch" className="button button-primary button-wide" disabled={busy} onClick={retryAccount}><RefreshCw />{t('appEntry.retryAccount')}</Button>}
      {user ? <Button variant="outline" size="touch" className="button button-quiet button-wide" disabled={busy} onClick={() => void switchAccount()}>{t('appEntry.switchAccount')}</Button>
        : <Link className="button button-quiet button-wide" to="/login" state={{ from: location.pathname + location.search + location.hash }}>{t('auth.signIn')}</Link>}
      {error && <p className="form-message error" role="alert">{error}</p>}
    </section>
  </main>;
}

export function AccountGate({ children }: { children: ReactNode }) {
  const { user, profile, loading, authError, emailVerificationRequired } = useAuth();
  const location = useLocation();
  if (loading || authError || (user && !emailVerificationRequired && profile?.uid !== user.uid)) return <AccountRecovery />;
  if (!user || emailVerificationRequired) return <Navigate to="/login" replace state={{ from: location.pathname + location.search + location.hash }} />;
  return <>{children}</>;
}
